/**
 * MarginNoteSidebar — Side-by-side annotation display for Focus Mode
 *
 * Renders annotations as margin notes on the right side, aligned
 * with the highlighted text ranges in the post body.
 * Replaces the hover-to-popover pattern with always-visible notes.
 */

import { useState } from 'react';
import type { ClubTheme } from './themes';
import type { SocialAnnotation } from './types';

interface MarginNoteSidebarProps {
  annotations: SocialAnnotation[];
  theme: ClubTheme;
  onStartDiscussion?: (annotationId: string) => void;
}

const TYPE_BADGES: Record<string, { emoji: string; label: string; color: string }> = {
  note: { emoji: '📝', label: 'Note', color: '#FBBF24' },
  'fact-check': { emoji: '🛡️', label: 'Fact Check', color: '#10B981' },
  question: { emoji: '❓', label: 'Question', color: '#3B82F6' },
};

const VERDICT_DISPLAY: Record<string, { emoji: string; color: string }> = {
  verified: { emoji: '✅', color: '#10B981' },
  disputed: { emoji: '⚠️', color: '#F59E0B' },
  unverifiable: { emoji: '❔', color: '#6B7280' },
  misleading: { emoji: '🚫', color: '#EF4444' },
};

export default function MarginNoteSidebar({ annotations, theme, onStartDiscussion }: MarginNoteSidebarProps) {
  const [expandedId, setExpandedId] = useState<string | null>(null);

  if (annotations.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center h-full p-4 text-center">
        <p className="text-xs" style={{ color: theme.colors.muted }}>
          No annotations yet. Select text in the post to add a note.
        </p>
      </div>
    );
  }

  // Sort by position in text
  const sorted = [...annotations].sort((a, b) => a.start_offset - b.start_offset);

  return (
    <div className="space-y-3 p-4 overflow-y-auto max-h-full">
      <h3
        className="text-[10px] font-bold uppercase tracking-widest mb-2"
        style={{ color: theme.colors.muted }}
      >
        Margin Notes ({annotations.length})
      </h3>

      {sorted.map((annotation) => {
        const badge = TYPE_BADGES[annotation.annotation_type] || TYPE_BADGES.note;
        const verdict = annotation.verdict ? VERDICT_DISPLAY[annotation.verdict] : null;
        const isExpanded = expandedId === annotation.id;

        return (
          <div
            key={annotation.id}
            className="rounded-xl p-3 transition-colors cursor-pointer"
            style={{
              backgroundColor: `${badge.color}10`,
              borderLeft: `3px solid ${badge.color}`,
            }}
            onClick={() => setExpandedId(isExpanded ? null : annotation.id)}
            role="button"
            tabIndex={0}
            onKeyDown={(e) => e.key === 'Enter' && setExpandedId(isExpanded ? null : annotation.id)}
          >
            {/* Header: type + author */}
            <div className="flex items-center gap-2 mb-1">
              <span className="text-xs">{badge.emoji}</span>
              <span
                className="text-[10px] font-semibold uppercase tracking-wide"
                style={{ color: badge.color }}
              >
                {badge.label}
              </span>
              {annotation.author && (
                <span className="text-[10px] ml-auto" style={{ color: theme.colors.muted }}>
                  {annotation.author.full_name}
                </span>
              )}
            </div>

            {/* Highlighted text reference */}
            <p
              className="text-[10px] italic line-clamp-1 mb-1"
              style={{ color: theme.colors.muted }}
            >
              &ldquo;{annotation.highlighted_text}&rdquo;
            </p>

            {/* Verdict badge (if fact-check) */}
            {verdict && (
              <div className="flex items-center gap-1 mb-1">
                <span className="text-xs">{verdict.emoji}</span>
                <span
                  className="text-[10px] font-semibold capitalize"
                  style={{ color: verdict.color }}
                >
                  {annotation.verdict}
                </span>
              </div>
            )}

            {/* Body */}
            {annotation.body && (
              <p
                className={`text-xs leading-relaxed ${isExpanded ? '' : 'line-clamp-2'}`}
                style={{ color: theme.colors.text }}
              >
                {annotation.body}
              </p>
            )}

            {/* Sources (expanded only) */}
            {isExpanded && annotation.sources?.length > 0 && (
              <div className="mt-2 space-y-1">
                {annotation.sources.map((src, i) => (
                  <a
                    key={i}
                    href={src.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="block text-[10px] truncate underline"
                    style={{ color: theme.colors.primary }}
                    onClick={(e) => e.stopPropagation()}
                  >
                    {src.title || src.url}
                  </a>
                ))}
              </div>
            )}

            {/* Discussion link */}
            {isExpanded && annotation.thread_post_id && onStartDiscussion && (
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  onStartDiscussion(annotation.id);
                }}
                className="mt-2 text-[10px] font-medium"
                style={{ color: theme.colors.primary }}
              >
                💬 View Discussion
              </button>
            )}
          </div>
        );
      })}
    </div>
  );
}
